import {
  createContext,
  FC,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UseAudioReactPlayer } from '@higo/ui/src/components/AudioPlayer/useAudioReactPlayer';

type GlobalActiveTrack = {
  key: string;
  pause: () => void;
};

let globalActiveTrack: GlobalActiveTrack | null = null;

const activateGlobalTrack = (key: string, pause: () => void) => {
  if (globalActiveTrack && globalActiveTrack.key !== key) {
    const previousTrack = globalActiveTrack;
    setTimeout(() => {
      previousTrack.pause();
    }, 0);
  }

  globalActiveTrack = { key, pause };
};

const clearGlobalTrack = (key: string) => {
  if (globalActiveTrack?.key === key) {
    globalActiveTrack = null;
  }
};

export interface AudioTrackPlaylistContext {
  activeTrack: string | null;
  setActiveTrack: (id: string | null) => void;
  onTrackEnd: (id: string) => void;
  register: (id: string, ref: UseAudioReactPlayer) => void;
  playPause: (id: string) => void;
}

const Context = createContext<AudioTrackPlaylistContext | null>(null);

export const useAudioTrackGroup = () => {
  return useContext(Context);
};

export interface AudioTrackGroupProviderProps {
  trackOrder?: string[];
}
export const AudioTrackGroupProvider: FC<AudioTrackGroupProviderProps> = memo(
  ({ trackOrder, children }) => {
    const [activeTrack, setActiveTrack] = useState<string | null>(null);
    const providerIdRef = useRef(`audio-track-group-${Math.random()}`);

    const refMap = useRef<Record<string, UseAudioReactPlayer>>({});

    const onTrackEnd = useCallback((id: string) => {
      refMap.current[id]?.pause();
      clearGlobalTrack(`${providerIdRef.current}:${id}`);
    }, []);

    useEffect(() => {
      let oldActiveTrack = activeTrack;
      if (activeTrack) {
        const activeKey = `${providerIdRef.current}:${activeTrack}`;
        const activePlayer = refMap.current[activeTrack];

        if (activePlayer) {
          activateGlobalTrack(activeKey, activePlayer.pause);
          activePlayer.play();
        }
      }

      return () => {
        // refMap holds tracks references and most likely won't change
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (oldActiveTrack) {
          refMap.current[oldActiveTrack]?.pause();
          clearGlobalTrack(`${providerIdRef.current}:${oldActiveTrack}`);
        }
      };
    }, [activeTrack]);

    const playPause = useCallback(
      (id: string | null) => {
        setActiveTrack(activeTrack === id ? null : id);
      },
      [activeTrack, setActiveTrack],
    );

    const register = useRef<AudioTrackPlaylistContext['register']>((id, x) => {
      refMap.current[id] = x;
    });

    const value = useMemo(
      () => ({
        activeTrack,
        setActiveTrack,
        onTrackEnd,
        register: register.current,
        playPause,
      }),
      [activeTrack, onTrackEnd, playPause],
    );

    return <Context.Provider value={value}>{children}</Context.Provider>;
  },
);
